import {Subject} from 'rxjs';

type Breakpoint = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'bd';

export class MediaQueryService {
    public whenChange: Subject<void> = new Subject();
    private static instance: MediaQueryService;
    private breakpoints: Breakpoints = {
        xxs: {width: 425},
        xs: {width: 576},
        sm: {width: 768},
        md: {width: 992},
        lg: {width: 1300},
        bd: {width: 1500},
    };
    private windowWidth: number = window.innerWidth;
    private lastBreakpoint: Breakpoint = this.currentBreakpoint();

    constructor() {
        window.addEventListener('resize', this.onWindowResize.bind(this));
    }

    public static getInstance(): MediaQueryService {
        if (!MediaQueryService.instance) {
            MediaQueryService.instance = new MediaQueryService();
        }
        return MediaQueryService.instance;
    }

    public get belowXxs(): boolean {
        return this.below('xxs');
    }

    public get aboveXxs(): boolean {
        return this.above('xxs');
    }

    public get belowXs(): boolean {
        return this.below('xs');
    }

    public get aboveXs(): boolean {
        return this.above('xs');
    }

    public get belowSm(): boolean {
        return this.below('sm');
    }

    public get aboveSm(): boolean {
        return this.above('sm');
    }

    public get belowMd(): boolean {
        return this.below('md');
    }

    public get aboveMd(): boolean {
        return this.above('md');
    }

    public get belowLg(): boolean {
        return this.below('lg');
    }

    public get aboveLg(): boolean {
        return this.above('lg');
    }

    public get belowBd(): boolean {
        return this.below('bd');
    }

    public get aboveBd(): boolean {
        return this.above('bd');
    }

    private above(breakpoint: Breakpoint): boolean {
        return this.windowWidth >= this.breakpoints[breakpoint].width;
    }

    private below(breakpoint: Breakpoint): boolean {
        return this.windowWidth < this.breakpoints[breakpoint].width;
    }

    private currentBreakpoint(): Breakpoint {
        const breakpoints: Breakpoint[] = Object.keys(this.breakpoints) as [keyof Breakpoints];
        return breakpoints.find((key: Breakpoint) => {
            return this.windowWidth <= this.breakpoints[key].width;
        }) || breakpoints[breakpoints.length - 1];
    }

    private onWindowResize(): void {
        this.windowWidth = window.innerWidth;
        const currentBreakpoint = this.currentBreakpoint();
        if (currentBreakpoint !== this.lastBreakpoint) {
            this.whenChange.next();
        }
        this.lastBreakpoint = currentBreakpoint;
    }
}

interface Breakpoints {
    xxs: BreakpointAttributes;
    xs: BreakpointAttributes,
    sm: BreakpointAttributes,
    md: BreakpointAttributes,
    lg: BreakpointAttributes,
    bd: BreakpointAttributes,
}

interface BreakpointAttributes {
    width: number;
}
